import { FC, useEffect, useState } from "react";
import { Dialog, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { APIS_KEYS, PropertyVerificationResult } from "@services";
import { ListManagementURL } from "../../utils/constants";
import { VerificationCard } from "./VerificationCard";
import { VerificationBadge } from "./VerificationBadge";
import { useUnverifiedProperties, UnverifiedProperty } from "./useUnverifiedProperties";

/**
 * MS-485 — the shared MT-verification modal. Two steps:
 *   1. (only when >1 property needs verifying) a grid of unverified properties to
 *      pick from;
 *   2. the shared VerificationCard for the selected property (persists via
 *      POST /verification/property/{id}/verify).
 * With exactly one unverified property — or when opened for a specific property
 * (table badge / row menu) — step 1 is skipped.
 */
export interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  /** Open straight on the card for this property (table badge / row menu). */
  propertyId?: number | null;
}

const C = {
  title: "#1F1B3A",
  muted: "#6B7280",
  primary: "#5B3FFF",
  border: "#E5E7EB",
  cardHover: "#5B3FFF",
};

export const VerificationModal: FC<VerificationModalProps> = ({
  open,
  onClose,
  propertyId = null,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const queryClient = useQueryClient();
  const { unverified } = useUnverifiedProperties();

  // Which property the card is showing. null → show the grid (multi only).
  const [manualSelectedId, setManualSelectedId] = useState<number | null>(null);

  // Reset the local step whenever the modal (re)opens.
  useEffect(() => {
    if (open) setManualSelectedId(null);
  }, [open, propertyId]);

  const singleId = unverified.length === 1 ? unverified[0].propertyId : null;
  const targetId = propertyId ?? manualSelectedId ?? singleId;
  const selected: UnverifiedProperty | undefined =
    targetId != null ? unverified.find((p) => p.propertyId === targetId) : undefined;

  // Back to the grid is only meaningful when the user picked from a grid.
  const cameFromGrid = propertyId == null && unverified.length > 1;
  const nothingToDo = propertyId == null && unverified.length === 0;
  const showGrid = !nothingToDo && targetId == null;
  const showCard = targetId != null;

  const refreshAfterVerify = () => {
    queryClient.invalidateQueries({ queryKey: [APIS_KEYS.GETUSERDETAILS] });
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey?.[0] === ListManagementURL,
    });
  };

  const handleVerified = (_res: PropertyVerificationResult) => {
    refreshAfterVerify();
    // One fewer property to verify. If the user came from the grid and more
    // remain, drop back to it; otherwise close.
    const remaining = unverified.filter((p) => p.propertyId !== targetId);
    if (cameFromGrid && remaining.length > 0) {
      setManualSelectedId(null);
    } else {
      onClose();
    }
  };

  const BackArrow = isRtl ? ArrowForwardIcon : ArrowBackIcon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={showGrid ? "md" : "sm"}
      fullWidth
      dir={isRtl ? "rtl" : "ltr"}
      PaperProps={{ sx: { borderRadius: "20px", p: { xs: "16px", sm: "24px" } } }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, textAlign: "start" }}>
          {!showGrid && cameFromGrid && (
            <IconButton size="small" onClick={() => setManualSelectedId(null)} sx={{ color: C.primary }}>
              <BackArrow fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.title }}>
              {showGrid
                ? t("PERMIT_VERIFICATION.MODAL_TITLE")
                : selected?.propertyName || t("PERMIT_VERIFICATION.MODAL_TITLE")}
            </Typography>
            {showGrid && (
              <Typography sx={{ fontSize: 13, color: C.muted, mt: "2px" }}>
                {t("PERMIT_VERIFICATION.MODAL_SUBTITLE_SELECT")}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: C.muted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      {showGrid ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "16px",
          }}
        >
          {unverified.map((p) => (
            <Box
              key={p.propertyId}
              onClick={() => setManualSelectedId(p.propertyId)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                p: "12px",
                border: `1px solid ${C.border}`,
                borderRadius: "16px",
                cursor: "pointer",
                transition: "border-color .15s, box-shadow .15s",
                textAlign: "start",
                "&:hover": {
                  borderColor: C.cardHover,
                  boxShadow: "0 2px 10px rgba(91,63,255,.12)",
                },
              }}
            >
              <Box
                component="img"
                src={
                  p.propertyImage ||
                  "https://a0.muscache.com/im/pictures/miso/Hosting-1113684001326676721/original/0b7671c8-41ae-4d31-8db3-d9ee6d7d29be.jpeg?aki_policy=x_medium"
                }
                alt=""
                sx={{ width: 56, height: 56, borderRadius: "12px", objectFit: "cover", flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.title, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.propertyName}
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.propertyLocation}
                </Typography>
                <Box sx={{ mt: "6px" }}>
                  <VerificationBadge
                    status={p.permitVerificationStatus}
                    expiryDate={p.permitExpiryDate}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : showCard ? (
        <VerificationCard
          surface="dashboard"
          propertyId={targetId as number}
          onVerified={handleVerified}
        />
      ) : (
        <Typography sx={{ fontSize: 14, color: C.muted, textAlign: "center", py: "24px" }}>
          {t("PERMIT_VERIFICATION.MODAL_ALL_DONE")}
        </Typography>
      )}
    </Dialog>
  );
};

export default VerificationModal;
